import { useState } from 'react';
import { Copy, Download, AlertTriangle, Check } from 'lucide-react';
import { useProjectStore } from '../../store/projectStore';
import { useTemplateEngine } from '../../hooks/useTemplateEngine';
import { getTemplateSourceLabel } from '../../lib/templateEngine';

const layerNames = ['Layer 1: 骨架層', 'Layer 2: 前台層', 'Layer 3: 後台層', 'Layer 4: 部署層'];

export function LayerOutput() {
  const config = useProjectStore((state) => state.config);
  const exportProject = useProjectStore((state) => state.exportProject);
  const layers = useTemplateEngine(config);
  const [activeLayer, setActiveLayer] = useState(0);
  const [copied, setCopied] = useState<number | null>(null);

  const activeResult = layers[activeLayer];
  const lineCount = activeResult.content.split(/\r?\n/).length;
  const wordCount = activeResult.content.length;

  const templateSource = getTemplateSourceLabel(config);

  const copyToClipboard = async (index: number) => {
    await navigator.clipboard.writeText(layers[index].content);
    setCopied(index);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Layer 輸出</h2>
          <span className="px-2 py-0.5 text-xs rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
            {templateSource}
          </span>
        </div>
        <button
          type="button"
          onClick={exportProject}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-blue-600 hover:bg-blue-700 text-white transition-colors"
        >
          <Download size={14} />
          導出全部
        </button>
      </div>

      <div className="flex flex-wrap gap-1 mb-4">
        {layers.map((result, index) => {
          const hasWarnings = result.unreplacedVariables.length > 0;
          return (
            <button
              key={index}
              type="button"
              onClick={() => setActiveLayer(index)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeLayer === index
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
              }`}
            >
              {layerNames[index]}
              {hasWarnings && <AlertTriangle size={14} className="text-yellow-300" />}
            </button>
          );
        })}
      </div>

      <div className="flex-1 flex flex-col min-h-0 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
          <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
            <span>{lineCount} 行</span>
            <span>{wordCount} 字</span>
            <span>{activeResult.moduleCount} 個模組</span>
            <span>{activeResult.variableCount} 個變數已替換</span>
          </div>
          <button
            type="button"
            onClick={() => copyToClipboard(activeLayer)}
            className="flex items-center gap-1.5 px-3 py-1 text-xs rounded-md border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            {copied === activeLayer ? <Check size={12} /> : <Copy size={12} />}
            {copied === activeLayer ? '已複製' : '複製'}
          </button>
        </div>

        {activeResult.unreplacedVariables.length > 0 && (
          <div className="px-4 py-2 bg-red-50 dark:bg-red-900/20 border-b border-red-100 dark:border-red-900/30">
            <p className="text-xs font-medium text-red-700 dark:text-red-300 flex items-center gap-1">
              <AlertTriangle size={14} />
              未替換變數：
            </p>
            <div className="mt-1 flex flex-wrap gap-1">
              {activeResult.unreplacedVariables.map((variable) => (
                <span
                  key={variable}
                  className="px-2 py-0.5 text-xs rounded bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300"
                >
                  {variable}
                </span>
              ))}
            </div>
          </div>
        )}

        <textarea
          readOnly
          value={activeResult.content}
          className="flex-1 w-full p-4 font-mono text-xs md:text-sm bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 resize-none focus:outline-none"
        />
      </div>
    </div>
  );
}
